/**
 * Analysis API Service
 *
 * Handles workflow configuration, analysis execution, status, and results
 */

import { apiClient } from './client';
import {
  WorkflowConfig,
  ConfigurationFormData,
  AnalysisResults,
  APIResponse
} from '../../types';

class AnalysisAPI {
  async configureWorkflow(projectId: string, config: ConfigurationFormData): Promise<any> {
    try {
      // Transform form data to backend format
      const workflowConfig: WorkflowConfig = {
        risk_tolerance: config.risk_tolerance,
        analysis_depth: config.analysis_depth,
        custom_instructions: config.custom_instructions || undefined,
        client_position: config.client_position || undefined,  // Issue #236: whose interests to protect
        // Set defaults for backward compatibility
        target_areas: [],
        document_type: 'contract'
      };

      const response = await apiClient.post(`/api/v1/projects/${projectId}/configure`, workflowConfig);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.detail || 'Invalid configuration data');
      } else if (error.response?.status === 404) {
        throw new Error('Project not found');
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  async getWorkflowConfig(projectId: string): Promise<WorkflowConfig> {
    try {
      // Tell axios that 404 is acceptable - prevents error logging for unconfigured projects
      const response = await apiClient.get<APIResponse>(
        `/api/v1/projects/${projectId}/config`,
        {
          validateStatus: (status) => status < 400 || status === 404
        }
      );

      // If 404, configuration doesn't exist yet (expected for new projects)
      if (response.status === 404) {
        throw new Error('Configuration not found');
      }

      // The API returns APIResponse with nested data.config
      return response.data.data.config as WorkflowConfig;
    } catch (error: any) {
      // If it's our "Configuration not found" error, re-throw it
      if (error.message === 'Configuration not found') {
        throw error;
      }
      // For other errors, check if there's a detail message
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  async startAnalysis(projectId: string, documentIds?: string[]): Promise<any> {
    const pipelineRequest: Record<string, any> = {
      use_pipeline: true,
      use_multi_agent: true, // Backward compat
      fallback_on_failure: true,
      budget_limit: 5.0,
      timeout_seconds: 600
    };

    // Issue #324: Scope analysis to specific documents
    if (documentIds && documentIds.length > 0) {
      pipelineRequest.document_ids = documentIds;
    }

    console.log('[DEBUG] Starting pipeline analysis with config:', pipelineRequest);
    try {
      const response = await apiClient.post(`/api/v1/projects/${projectId}/analyze/pipeline`, pipelineRequest);
      console.log('[DEBUG] Pipeline analysis request successful:', response.data);
      return response.data;
    } catch (error: any) {
      // Plan 145 phase 3 (#679): admission control returns 413 with a specific
      // detail string. Surface that exact message so users can see whether
      // they're over the per-doc, aggregate, or document-count cap.
      if (error.response?.status === 413 && error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  async getAnalysisStatus(projectId: string): Promise<any> {
    console.log(`[API] Getting analysis status for project ${projectId}`);
    const response = await apiClient.get(`/api/v1/projects/${projectId}/analysis/status`);
    console.log(`[API] Analysis status successful:`, response.status);
    return response.data;
  }

  async getAnalysisResults(projectId: string): Promise<AnalysisResults> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/analysis/results`);
    return response.data;
  }

  /**
   * Remove a document's review from view. Nothing is erased -- the assessment
   * and its findings are retained, and the review is hidden from every Review
   * surface. Plan 244 D3 (#1030); the DELETE verb and URI are kept for
   * compatibility.
   * @param projectId - The workflow project ID (from layerStore.layerSourceProjectIds)
   * @param documentId - The document whose review is hidden
   */
  async removeDocumentReview(projectId: string, documentId: string): Promise<APIResponse> {
    const response = await apiClient.delete(`/api/v1/projects/${projectId}/documents/${documentId}/review`);
    return response.data;
  }

  // Task 20: Comment functionality
  async addIssueComment(issueId: string, comment: string): Promise<APIResponse> {
    const response = await apiClient.post(`/api/v1/issues/${issueId}/comment`, { comment });
    return response.data;
  }

  async getIssueComment(issueId: string): Promise<APIResponse> {
    const response = await apiClient.get(`/api/v1/issues/${issueId}/comment`);
    return response.data;
  }

  async deleteIssueComment(issueId: string): Promise<APIResponse> {
    const response = await apiClient.delete(`/api/v1/issues/${issueId}/comment`);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw new Error('Backend service is not available');
    }
  }
}

export const analysisApi = new AnalysisAPI();
